/**
 * APSkeleton Component
 *
 * A skeleton loading component that displays placeholder content while actual content is loading.
 * Supports multiple variants, animations, and is fully customizable.
 *
 * @example
 * ```tsx
 * // Text skeleton
 * <APSkeleton variant="text" width="80%" />
 *
 * // Circular avatar skeleton
 * <APSkeleton variant="circular" width={40} height={40} />
 *
 * // Rectangular image skeleton
 * <APSkeleton variant="rectangular" width="100%" height={200} />
 *
 * // Custom skeleton with wave animation
 * <APSkeleton variant="rounded" animation="wave" width="100%" height={60} />
 * ```
 */

import { forwardRef } from 'react';

import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPSkeletonProps } from './types';

import './styles.scss';

/**
 * APSkeleton - Production-ready skeleton loading component
 *
 * Follows AstroPay design system and provides smooth loading states.
 */
export const APSkeleton = forwardRef<HTMLDivElement, IAPSkeletonProps>(
  (
    {
      variant = 'text',
      animation = 'pulse',
      width,
      height,
      className = '',
      style,
      children,
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    const translate = useTranslate();
    // Construct class names using BEM methodology
    const baseClasses = 'ap-skeleton';
    const variantClasses = `ap-skeleton--${variant}`;
    const animationClasses = animation ? `ap-skeleton--${animation}` : '';

    const skeletonClasses = [baseClasses, variantClasses, animationClasses, className]
      .filter(Boolean)
      .join(' ');

    // Prepare inline styles
    const skeletonStyle: React.CSSProperties = {
      ...style,
    };

    // Apply width if provided
    if (width !== undefined) {
      skeletonStyle.width = typeof width === 'number' ? `${width}px` : width;
    }

    // Apply height if provided
    if (height !== undefined) {
      skeletonStyle.height = typeof height === 'number' ? `${height}px` : height;
    }

    return (
      <div
        ref={ref}
        className={skeletonClasses}
        style={skeletonStyle}
        data-testid={testId}
        aria-label={translate('status.loading')}
        role="progressbar"
        aria-busy="true"
        {...rest}
      >
        {children}
      </div>
    );
  }
);

APSkeleton.displayName = 'APSkeleton';

export type { IAPSkeletonProps };
export default APSkeleton;
